import { CircularProgress, ThemeProvider } from '@mui/material'
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import {
    Navigate,
    Route,
    Routes,
    useLocation,
    useNavigate,
    useSearchParams,
} from 'react-router-dom'
import Layout from './components/Layout'
import { AccountContextProvider, useAccount } from './context/account_context'
import { BranchContextProvider, useBranch } from './context/branch_context'
import { BuyContextProvider } from './context/buy_context'
import { CashierContextProvider, useCashier } from './context/cashier_context'
import { ClientProvider } from './context/client_context'
import { CoinContextProvider, useCoin } from './context/coin_context'
import {
    ConfigsContextProvider,
    configsDefault,
    ConfigsInfo,
    useConfigs,
} from './context/configs_context'
import { DiscountContextProvider } from './context/discount_context'
import { LoadingProvider, useLoading } from './context/loading_context'
import { NoteContextProvider } from './context/notes_context'
import { PasswordContextProvider } from './context/password_context'
import {
    PaymentMethodContextProvider,
    usePaymentMethod,
} from './context/payment_method_context'
import { ProductProvider, useProduct } from './context/product_context'
import { SaleContextProvider } from './context/sale_context'
import { ServiceContextProvider } from './context/service_context'
import { SnackProvider, useSnack } from './context/snack_context'
import { SpentContextProvider } from './context/spent_context'
import { SupplierProvider } from './context/supplier_context'

import { LoadStockContextProvider } from './context/load_stock_context'
import { RenderNote } from './pages/RenderNote'
import { doLogout, doRefreshToken, isLogged } from './services/auth'
import { getLastFrontEndVersion, loadInitialData } from './services/utils'
import { LayoutConfigsInterface } from './components/LayoutConfigs'
import { createAppTheme } from './theme'
import { useThemeMode } from './context/theme_mode_context'
import Log from './pages/Log'

const Home = lazy(() => import('./pages/Home'))
const Accounts = lazy(() => import('./pages/Accounts'))
const Branch = lazy(() => import('./pages/Branch'))
const Buys = lazy(() => import('./pages/Buy'))
const Cashier = lazy(() => import('./pages/Cashier'))
const Clients = lazy(() => import('./pages/Clients'))
const Coins = lazy(() => import('./pages/Coin'))
const Configs = lazy(() => import('./pages/Configs'))
const Notes = lazy(() => import('./pages/Note'))
const PaymentMethod = lazy(() => import('./pages/PaymentMethod'))
const Products = lazy(() => import('./pages/Products'))
const Sales = lazy(() => import('./pages/Sale'))
const Spents = lazy(() => import('./pages/Spent'))
const Supplier = lazy(() => import('./pages/Supplier'))
const Documents = lazy(() => import('./pages/Document'))
const Services = lazy(() => import('./pages/Service'))
const Employee = lazy(() => import('./pages/Employee'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Vehicle = lazy(() => import('./pages/Vehicle'))
const Bank = lazy(() => import('./pages/Bank'))

function ContextDataLoader({ children }: any) {
    const loading = useLoading()
    const snack = useSnack()
    const { setConfigs, setContactTypes } = useConfigs()
    const { setPaymentMethods } = usePaymentMethod()
    const { setAccountInfo, setAccountRules } = useAccount()
    const { setCoins, setDefaultExchanges } = useCoin()
    const { setLoggedCashier, setCashiers } = useCashier()
    const { setBranches } = useBranch()
    const { setProductInfo, setPriceList } = useProduct()

    useEffect(() => {
        if (!isLogged()) return
        handleInitData()
    }, [])

    const handleInitData = async () => {
        loading.show()
        try {
            const {
                configs,
                contactTypes,
                paymentMethods,
                coins,
                rules,
                user,
                cashiers,
                branches,
                loggedCashier,
                groups,
                marks,
                priceList,
            } = await loadInitialData()

            setConfigs(configs)
            setContactTypes(contactTypes)
            setPaymentMethods(paymentMethods)
            setAccountInfo(user)
            setAccountRules(rules)
            setCoins(setDefaultExchanges(coins))
            setLoggedCashier(loggedCashier)
            setCashiers(cashiers)
            setBranches(branches)
            setProductInfo((prev) => ({
                ...prev,
                groups,
                marks,
            }))
            setPriceList(priceList)
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.hide()
    }

    return <>{children}</>
}

function System() {
    return (
        <BranchContextProvider>
            <PaymentMethodContextProvider>
                <ClientProvider>
                    <SupplierProvider>
                        <ProductProvider>
                            <NoteContextProvider>
                                <DiscountContextProvider>
                                    <LoadStockContextProvider>
                                        <BuyContextProvider>
                                            <PasswordContextProvider>
                                                <SaleContextProvider>
                                                    <SpentContextProvider>
                                                        <ServiceContextProvider>
                                                            <ContextDataLoader>
                                                                <Routes>
                                                                    <Route
                                                                        path="/product/*"
                                                                        element={
                                                                            <Products />
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path="/client/*"
                                                                        element={
                                                                            <Clients />
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path="/supplier/*"
                                                                        element={
                                                                            <Supplier />
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path="/buy/*"
                                                                        element={
                                                                            <Buys />
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path="/sale/*"
                                                                        element={
                                                                            <Sales />
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path="/payment-method/*"
                                                                        element={
                                                                            <PaymentMethod />
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path="/note/*"
                                                                        element={
                                                                            <Notes />
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path="/spent/*"
                                                                        element={
                                                                            <Spents />
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path="/service/*"
                                                                        element={
                                                                            <Services />
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path="/coin/*"
                                                                        element={
                                                                            <Coins />
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path="/employee/*"
                                                                        element={
                                                                            <Employee />
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path="/document/*"
                                                                        element={
                                                                            <Documents />
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path="/account/*"
                                                                        element={
                                                                            <Accounts />
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path="/cashier/*"
                                                                        element={
                                                                            <Cashier />
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path="/vehicle/*"
                                                                        element={
                                                                            <Vehicle />
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path="/dashboard"
                                                                        element={
                                                                            <Dashboard />
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path="/bank/*"
                                                                        element={
                                                                            <Bank />
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path="/branch/*"
                                                                        element={
                                                                            <Branch />
                                                                        }
                                                                    />

                                                                    <Route
                                                                        path="/configs"
                                                                        element={
                                                                            <Configs />
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path="/render-note"
                                                                        element={
                                                                            <RenderNote />
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path="/log"
                                                                        element={
                                                                            <Log />
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path="*"
                                                                        element={
                                                                            <Navigate to="product" />
                                                                        }
                                                                    />
                                                                </Routes>
                                                            </ContextDataLoader>
                                                        </ServiceContextProvider>
                                                    </SpentContextProvider>
                                                </SaleContextProvider>
                                            </PasswordContextProvider>
                                        </BuyContextProvider>
                                    </LoadStockContextProvider>
                                </DiscountContextProvider>
                            </NoteContextProvider>
                        </ProductProvider>
                    </SupplierProvider>
                </ClientProvider>
            </PaymentMethodContextProvider>
        </BranchContextProvider>
    )
}

function App() {
    const location = useLocation()
    const navigate = useNavigate()
    const { mode } = useThemeMode()
    const [configs, setConfigs] = useState<ConfigsInfo>(configsDefault)
    const refreshTokenTimeout = useRef<NodeJS.Timeout>()
    const versionVerificationTimeout = useRef<NodeJS.Timeout>()
    const windowRefreshTimeout = useRef<NodeJS.Timeout>()
    const lastVersion = useRef<Date>()
    const needToRefresh = useRef<boolean>(false)
    const isInactive = useRef<boolean>(false)
    const [searchParams] = useSearchParams()

    useEffect(() => {
        if (process.env.REACT_APP_MODE !== 'cloud') return

        const script = document.createElement('script')
        script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`
        document.body.appendChild(script)

        const events = ['mousemove', 'keydown', 'scroll', 'click']

        events.forEach((event) => {
            document.addEventListener(event, () => {
                isInactive.current = false
                clearTimeout(windowRefreshTimeout.current)
                windowRefreshTimeout.current = setTimeout(
                    () => {
                        isInactive.current = true
                    },
                    5 * 60 * 1000
                )
            })
        })

        versionVerificationTimeout.current = setInterval(async () => {
            const versionResponse = await getLastFrontEndVersion()
            if (
                lastVersion.current &&
                versionResponse.valueOf() > lastVersion.current.valueOf()
            )
                needToRefresh.current = true
            lastVersion.current = versionResponse
            if (isInactive.current && needToRefresh.current)
                window.location.reload()
        }, 1000 * 60)

        return () => {
            clearInterval(versionVerificationTimeout.current)
            clearTimeout(windowRefreshTimeout.current)
        }
    }, [])

    useEffect(() => {
        const logged = isLogged()

        if (!logged) {
            doLogout()
            if (location.pathname.includes('/system')) navigate('/')
        } else {
            if (location.pathname === '/') navigate('/system')
        }
    }, [location.pathname])

    useEffect(() => {
        const lockOnSaleScreen = searchParams.get('lockOnSaleScreen')
        if (lockOnSaleScreen)
            localStorage.setItem('lockOnSaleScreen', lockOnSaleScreen)

        handleRefreshToken()
        refreshTokenTimeout.current = setInterval(
            () => {
                handleRefreshToken()
            },
            1000 * 60 * 60
        )

        return () => {
            clearTimeout(refreshTokenTimeout.current)
        }
    }, [])

    const handleRefreshToken = async () => {
        if (!isLogged()) return

        try {
            await doRefreshToken()
        } catch (error) {
            /* doLogout()
            navigate('/login') */
        }
    }

    const theme = useMemo(() => {
        let primaryColor
        let hoverColor
        if (configs.layoutConfigs) {
            const layoutConfigs = JSON.parse(
                configs.layoutConfigs
            ) as LayoutConfigsInterface
            if (layoutConfigs.general.primaryColor)
                primaryColor = layoutConfigs.general.primaryColor
            if (layoutConfigs.general.hoverColor)
                hoverColor = layoutConfigs.general.hoverColor
        }

        return createAppTheme({ primaryColor, hoverColor, mode })
    }, [configs, mode])

    return (
        <Suspense
            fallback={
                <CircularProgress
                    sx={{
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        position: 'absolute',
                    }}
                />
            }
        >
            <ThemeProvider theme={theme}>
                <LoadingProvider>
                    <SnackProvider>
                        <ConfigsContextProvider onChange={setConfigs}>
                            <CoinContextProvider>
                                <AccountContextProvider>
                                    <CashierContextProvider>
                                        <Layout>
                                            <Routes>
                                                <Route
                                                    path="/"
                                                    element={<Home />}
                                                />
                                                <Route
                                                    path="/system/*"
                                                    element={<System />}
                                                />
                                                <Route
                                                    path="*"
                                                    element={
                                                        <Navigate to="/" />
                                                    }
                                                />
                                            </Routes>
                                        </Layout>
                                    </CashierContextProvider>
                                </AccountContextProvider>
                            </CoinContextProvider>
                        </ConfigsContextProvider>
                    </SnackProvider>
                </LoadingProvider>
            </ThemeProvider>
        </Suspense>
    )
}

export default App
